import axios from "axios";
import type { AxiosResponse } from "axios";
import { adminApiUrl, publicApiUrl, signedInApiUrl } from "../util/Constants";
import { playerToString } from "../util/StringUtils";
import type { ChatMention, ChatMessage } from "../types/domain";

const CHAT_API = "/chat";

interface GetMessagesParams {
  page: number;
  size: number;
  sort?: string;
  tournamentId?: number | null;
}

export interface LatestTimestampResponse {
  timestamp: string | null;
  mentionTimestamp: string | null;
}

export const chatApi = {
  getMessages: async ({ page, size, sort = "id,desc", tournamentId }: GetMessagesParams): Promise<ChatMessage[]> => {
    const { data } = await axios.get<ChatMessage[]>(`${publicApiUrl}${CHAT_API}/messages`, {
      params: { page, size, sort, ...(tournamentId != null && { tournamentId }) },
    });
    data.forEach((msg) => {
      msg.authorName = playerToString(msg.author, false, true);
    });
    return data;
  },

  getLatestTimestamp: async (tournamentId?: number | null): Promise<LatestTimestampResponse> => {
    const { data } = await axios.get<LatestTimestampResponse>(`${publicApiUrl}${CHAT_API}/latest-timestamp`, {
      params: tournamentId != null ? { tournamentId } : {},
    });
    return data;
  },

  getMentionablePlayers: async (): Promise<ChatMention[]> => {
    const { data } = await axios.get<ChatMention[]>(`${publicApiUrl}${CHAT_API}/mentionable-players`);
    return data;
  },

  sendMessage: async (
    content: string,
    tournamentId?: number | null,
    mentionedPlayerIds: number[] = [],
    replyToMessageId?: number | null,
  ): Promise<ChatMessage> => {
    const { data } = await axios.post<ChatMessage>(
      `${signedInApiUrl}${CHAT_API}/messages`,
      { content, mentionedPlayerIds, replyToMessageId: replyToMessageId ?? null },
      { params: tournamentId != null ? { tournamentId } : {} },
    );
    data.authorName = playerToString(data.author, false, true);
    return data;
  },

  editMessage: async (id: number, content: string, mentionedPlayerIds: number[] = []): Promise<ChatMessage> => {
    const { data } = await axios.patch<ChatMessage>(
      `${signedInApiUrl}${CHAT_API}/messages/${id}`,
      { content, mentionedPlayerIds },
    );
    data.authorName = playerToString(data.author, false, true);
    return data;
  },

  getPinned: async (tournamentId?: number | null): Promise<ChatMessage[]> => {
    const { data } = await axios.get<ChatMessage[]>(`${publicApiUrl}${CHAT_API}/pinned`, {
      params: tournamentId != null ? { tournamentId } : {},
    });
    return data;
  },

  pinMessage: async (id: number): Promise<ChatMessage> => {
    const { data } = await axios.post<ChatMessage>(`${adminApiUrl}${CHAT_API}/messages/${id}/pin`);
    return data;
  },

  unpinMessage: async (id: number): Promise<ChatMessage> => {
    const { data } = await axios.post<ChatMessage>(`${adminApiUrl}${CHAT_API}/messages/${id}/unpin`);
    return data;
  },

  deleteAsAdmin: async (id: number): Promise<AxiosResponse> => {
    return axios.delete(`${adminApiUrl}${CHAT_API}/messages/${id}`);
  },

  deleteOwn: async (id: number): Promise<AxiosResponse> => {
    return axios.delete(`${signedInApiUrl}${CHAT_API}/messages/${id}`);
  },

  addReaction: async (id: number, emoji?: string): Promise<AxiosResponse> => {
    return axios.post(`${signedInApiUrl}${CHAT_API}/messages/${id}/like`, null, {
      params: emoji ? { emoji } : {},
    });
  },

  removeReaction: async (id: number, emoji?: string): Promise<AxiosResponse> => {
    return axios.delete(`${signedInApiUrl}${CHAT_API}/messages/${id}/unlike`, {
      params: emoji ? { emoji } : {},
    });
  },
};
